// Shared browser-DuckDB pipeline for Forecast ML package artifacts.
//
// Forecast packages publish their tables as parquet entries inside one ZIP
// bundle; this module warms them into package-scoped DuckDB tables behind
// stable logical views, layers business-keyed user deltas on top via
// `_effective` views, and keeps the localStorage snapshot cache. Extracted
// from CfoForecastPage so every forecast surface (Forecast Studio, Insights,
// Plans, Cash, Scenarios) shares one loader.
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  QuickBooksFpaMlForecastPackageSnapshotResponse,
  QuickBooksFpaMlUserDelta,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import { trimStr } from './cfoFormat'

export const FORECAST_STUDIO_SNAPSHOT_CACHE_VERSION = 2

export const FORECAST_STUDIO_SNAPSHOT_CACHE_PREFIX = '__fg_forecast_studio_snapshot_v2__'

export const FORECAST_STUDIO_SNAPSHOT_CACHE_MAX_BYTES = 4_000_000

export const FORECAST_STUDIO_BACKGROUND_ARTIFACT_TIMEOUT_MS = 180_000

export const FORECAST_STUDIO_SMALL_ARTIFACT_BUFFER_THRESHOLD_BYTES = 128 * 1024

export type ForecastArtifactKey =
  | 'package_manifest'
  | 'line_metadata'
  | 'cash_daily'
  | 'cash_weekly'
  | 'layout_node'
  | 'driver_impacts'
  | 'driver_assumptions'

export const FORECAST_STUDIO_REQUIRED_ARTIFACT_KEYS: ForecastArtifactKey[] = [
  'package_manifest',
  'line_metadata',
  'cash_daily',
  'layout_node',
  'driver_assumptions',
]

export const FORECAST_STUDIO_ARTIFACTS: Array<[string, ForecastArtifactKey]> = [
  ['fg_qbo_fpa_forecast_package_snapshot', 'package_manifest'],
  ['fg_qbo_fpa_forecast_line_metadata', 'line_metadata'],
  ['fg_qbo_fpa_forecast_cash_daily', 'cash_daily'],
  ['fg_qbo_fpa_forecast_cash_weekly', 'cash_weekly'],
  ['fg_qbo_fpa_forecast_layout_node', 'layout_node'],
  ['fg_qbo_fpa_forecast_driver_impacts', 'driver_impacts'],
  ['fg_qbo_fpa_forecast_driver_assumptions', 'driver_assumptions'],
]

// cash_weekly/driver_impacts only ship from artifact-materialization version 2
// (the single-ZIP bundle); version-1 packages must keep validating.
export const forecastRequiredArtifactKeys = (
  snapshot: Pick<QuickBooksFpaMlForecastPackageSnapshotResponse, 'artifact_materialization'> | null | undefined,
): ForecastArtifactKey[] => {
  const version = Number(snapshot?.artifact_materialization?.version ?? 1)
  return version >= 2
    ? [...FORECAST_STUDIO_REQUIRED_ARTIFACT_KEYS, 'cash_weekly']
    : FORECAST_STUDIO_REQUIRED_ARTIFACT_KEYS
}

// Artifact tables warm into package-scoped physical tables so switching
// anchors never reads another package's rows; queries keep using the stable
// base name through a logical view that tracks the selected package.
export const forecastPackageScopedTableName = (baseTable: string, packageId: string | null | undefined): string => {
  const scope = trimStr(packageId).replace(/[^a-zA-Z0-9]/g, '_')
  return scope ? `${baseTable}__${scope}` : baseTable
}

export const isDuckDbAccessHandleConflict = (err: any): boolean =>
  /createSyncAccessHandle|sync access handle|access handle|NoModificationAllowedError/i.test(trimStr(err?.message || err))

export const forecastStudioPerf = (event: string, payload: Record<string, any> = {}): void => {
  try {
    console.debug('[forecast-studio-perf]', event, payload)
  } catch {}
}

// scenarioKey is part of the key: a cached base snapshot must never be reused
// for upside/downside (the snapshot's scenario_key drives every DuckDB filter).
export const forecastStudioSnapshotCacheKey = (
  workbookId: string,
  selectedEndPeriod: string | null | undefined,
  futureMonths: number,
  mode: string,
  scenarioKey: string | null | undefined = 'base',
  packageId?: string | null,
): string => {
  const base = `${FORECAST_STUDIO_SNAPSHOT_CACHE_PREFIX}:${workbookId}:${selectedEndPeriod || 'active'}:${futureMonths}:${mode}:${trimStr(scenarioKey) || 'base'}`
  const scopedPackageId = trimStr(packageId)
  return scopedPackageId ? `${base}:pkg:${scopedPackageId}` : base
}

export const forecastStudioSnapshotSignature = (snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse | null | undefined): string => {
  const artifactMaterialization = snapshot?.artifact_materialization || {}
  const artifacts = artifactMaterialization?.artifacts || {}
  const artifactKeys = Object.keys(artifacts).sort()
  return JSON.stringify({
    version: FORECAST_STUDIO_SNAPSHOT_CACHE_VERSION,
    package_id: snapshot?.package?.id || null,
    package_updated_at: snapshot?.package?.updated_at || null,
    package_start_period: snapshot?.package?.start_period || null,
    package_end_period: snapshot?.package?.end_period || null,
    as_of_date: snapshot?.as_of_date || null,
    start_date: snapshot?.start_date || null,
    end_date_exclusive: snapshot?.end_date_exclusive || null,
    artifact_materialized_at: artifactMaterialization?.materialized_at || null,
    artifacts: artifactKeys.map((key) => ({
      key,
      rows: artifacts[key]?.rows ?? null,
      bytes: artifacts[key]?.bytes ?? null,
      extract_version_id: artifacts[key]?.extract_version_id ?? null,
      completed_at: artifacts[key]?.completed_at ?? null,
    })),
  })
}

export const forecastSnapshotHasRequiredArtifacts = (snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse | null | undefined): boolean => {
  const artifactUrls = snapshot?.artifact_urls || {}
  const artifacts = snapshot?.artifact_materialization?.artifacts || {}
  return forecastRequiredArtifactKeys(snapshot).every((artifactKey) => {
    const rows = Number(artifacts?.[artifactKey]?.rows)
    return Boolean(artifactUrls?.[artifactKey]) && Number.isFinite(rows) && rows > 0
  })
}

export const loadForecastStudioSnapshotCache = (cacheKey: string): QuickBooksFpaMlForecastPackageSnapshotResponse | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null
    const parsed = JSON.parse(window.localStorage.getItem(cacheKey) || 'null')
    if (!parsed || parsed.version !== FORECAST_STUDIO_SNAPSHOT_CACHE_VERSION || !parsed.snapshot) return null
    if (forecastStudioSnapshotSignature(parsed.snapshot) !== parsed.signature) return null
    if (!forecastSnapshotHasRequiredArtifacts(parsed.snapshot)) return null
    return parsed.snapshot
  } catch {
    return null
  }
}

export const saveForecastStudioSnapshotCache = (cacheKey: string, snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage || !snapshot?.package?.id) return
    const payload = JSON.stringify({
      version: FORECAST_STUDIO_SNAPSHOT_CACHE_VERSION,
      saved_at: new Date().toISOString(),
      signature: forecastStudioSnapshotSignature(snapshot),
      snapshot,
    })
    if (payload.length > FORECAST_STUDIO_SNAPSHOT_CACHE_MAX_BYTES) return
    window.localStorage.setItem(cacheKey, payload)
  } catch {
    // Storage can be disabled or quota-limited; Forecast Studio still works from server artifacts.
  }
}

export const clearForecastStudioSnapshotCache = (cacheKey: string): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    window.localStorage.removeItem(cacheKey)
  } catch {
    // Storage can be disabled; cache eviction is best-effort only.
  }
}

export const forecastArtifactUrl = (
  appApi: ServerApiClient,
  workbookId: string | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  artifactKey: string,
): string | null => {
  const wb = trimStr(workbookId)
  if (!wb || !snapshot?.package?.id) return null
  if (!snapshot.artifact_urls?.[artifactKey]) return null
  return appApi.getQuickBooksFpaMlForecastPackageArtifactParquetUrl(wb, artifactKey, snapshot.package.id)
}

export const snapshotHasParquetArtifacts = (snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse | null | undefined): boolean =>
  forecastSnapshotHasRequiredArtifacts(snapshot)

export const forecastArtifactCacheKey = (
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  artifactKey: string,
): string => {
  const artifact = snapshot?.artifact_materialization?.artifacts?.[artifactKey] || {}
  return [
    snapshot?.package?.id || '',
    artifactKey,
    artifact.extract_version_id || '',
    artifact.rows ?? '',
    artifact.bytes ?? '',
    snapshot?.artifact_materialization?.materialized_at || '',
    artifact.completed_at || '',
  ].join('|')
}

export const forecastArtifactWarmCacheKey = (
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  tableName: string,
  artifactKey: string,
): string => `${tableName}|${forecastArtifactCacheKey(snapshot, artifactKey)}`

export const forecastDuckDbStatementOpts = (duckDbOpts: Record<string, any> | null | undefined, caller: string): Record<string, any> => ({
  ...(duckDbOpts || {}),
  allowMutating: true,
  allow_mutating: true,
  timeoutMs: 30000,
  caller,
})

const forecastDuckDbRowsAsObjects = (result: any): Record<string, any>[] => {
  const headers: string[] = Array.isArray(result?.headers) ? result.headers : []
  const previewRows: any[] = Array.isArray(result?.preview_rows)
    ? result.preview_rows
    : Array.isArray(result?.previewRows)
      ? result.previewRows
      : Array.isArray(result?.rows)
        ? result.rows
        : []
  return previewRows.map((row) => {
    if (row && !Array.isArray(row) && typeof row === 'object') return row
    const out: Record<string, any> = {}
    headers.forEach((header, index) => {
      out[header] = Array.isArray(row) ? row[index] : undefined
    })
    return out
  })
}

const forecastSqlLiteral = (value: string): string => `'${String(value || '').replace(/'/g, "''")}'`

const forecastRelationKind = async (
  workerClient: AppWorkerClient,
  relationName: string,
  duckDbOpts: Record<string, any> | null | undefined,
  caller = 'QuickBooksCfoCore:forecast_relation_kind',
): Promise<'table' | 'view' | null> => {
  if (typeof workerClient.duckdb_run_sql !== 'function') return null
  const result = await workerClient.duckdb_run_sql(`
    SELECT table_type
    FROM information_schema.tables
    WHERE table_schema = 'main'
      AND table_name = ${forecastSqlLiteral(relationName)}
    LIMIT 1
  `, {
    ...(duckDbOpts || {}),
    previewRowLimit: 1,
    timeoutMs: 10000,
    caller,
  })
  const tableType = trimStr(forecastDuckDbRowsAsObjects(result)?.[0]?.table_type).toLowerCase()
  if (tableType.includes('view')) return 'view'
  if (tableType) return 'table'
  return null
}

export const forecastRelationExists = async (
  workerClient: AppWorkerClient | null | undefined,
  relationName: string,
  duckDbOpts: Record<string, any> | null | undefined,
): Promise<boolean> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') return false
  try {
    return Boolean(await forecastRelationKind(workerClient, relationName, duckDbOpts))
  } catch {
    return false
  }
}

export const dropForecastRelationIfExists = async (
  workerClient: AppWorkerClient,
  relationName: string,
  duckDbOpts: Record<string, any> | null | undefined,
  caller = 'QuickBooksCfoCore:forecast_relation_drop',
): Promise<void> => {
  const opts = forecastDuckDbStatementOpts(duckDbOpts, caller)
  const kind = await forecastRelationKind(workerClient, relationName, duckDbOpts, caller)
  if (kind === 'view') {
    await workerClient.duckdb_run_sql(`DROP VIEW "${relationName}"`, opts)
  } else if (kind === 'table') {
    await workerClient.duckdb_run_sql(`DROP TABLE "${relationName}"`, opts)
  }
}

// Point the stable logical name at the package-scoped physical table. The
// legacy physical table with the base name (from older sessions) must be
// dropped first or the view creation conflicts.
export const ensureForecastLogicalView = async (
  workerClient: AppWorkerClient,
  baseTable: string,
  physicalTable: string,
  duckDbOpts: Record<string, any> | null | undefined,
): Promise<void> => {
  if (baseTable === physicalTable || typeof workerClient.duckdb_run_sql !== 'function') return
  const opts = forecastDuckDbStatementOpts(duckDbOpts, 'QuickBooksCfoCore:forecast_artifact_view')
  await dropForecastRelationIfExists(workerClient, baseTable, duckDbOpts, 'QuickBooksCfoCore:forecast_artifact_view_drop')
  await workerClient.duckdb_run_sql(`CREATE OR REPLACE VIEW "${baseTable}" AS SELECT * FROM "${physicalTable}"`, opts)
}

export const releaseForecastScopedTable = async (
  workerClient: AppWorkerClient,
  physicalTable: string,
  duckDbOpts: Record<string, any> | null | undefined,
): Promise<void> => {
  if (typeof workerClient.duckdb_run_sql !== 'function') return
  try {
    await dropForecastRelationIfExists(workerClient, physicalTable, duckDbOpts, 'QuickBooksCfoCore:forecast_artifact_release')
  } catch {
    // Best-effort: a busy handle is retried by the caller.
  }
}

export const releaseForecastPackageScopedTables = async (
  workerClient: AppWorkerClient | null | undefined,
  packageId: string | null | undefined,
  duckDbOpts: Record<string, any> | null | undefined,
): Promise<void> => {
  if (!workerClient || !trimStr(packageId)) return
  for (const [tableName] of FORECAST_STUDIO_ARTIFACTS) {
    await releaseForecastScopedTable(workerClient, forecastPackageScopedTableName(tableName, packageId), duckDbOpts)
  }
}

export const dropForecastLogicalViews = async (
  workerClient: AppWorkerClient,
  duckDbOpts: Record<string, any> | null | undefined,
): Promise<void> => {
  if (typeof workerClient.duckdb_run_sql !== 'function') return
  for (const [tableName] of FORECAST_STUDIO_ARTIFACTS) {
    try {
      await dropForecastRelationIfExists(workerClient, tableName, duckDbOpts, 'QuickBooksCfoCore:forecast_artifact_view_drop')
    } catch {
      // Best-effort cleanup before row fallback.
    }
  }
}

export const warmForecastSnapshotInDuckDb = async (
  appApi: ServerApiClient,
  workerClient: AppWorkerClient | null | undefined,
  workbookId: string | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  duckDbOpts: Record<string, any> | null,
  warmOpts?: {
    artifactKeys?: ForecastArtifactKey[]
    timeoutMs?: number
    phase?: string
    inFlight?: Map<string, Promise<void>>
    completed?: Set<string>
  },
): Promise<void> => {
  if (!workerClient) return
  if (snapshotHasParquetArtifacts(snapshot) && typeof workerClient.duckdb_create_table_from_parquet_urls === 'function') {
    const wanted = Array.isArray(warmOpts?.artifactKeys) ? new Set(warmOpts?.artifactKeys) : null
    const inFlight = warmOpts?.inFlight
    const completed = warmOpts?.completed
    const packageId = trimStr(snapshot.package?.id)
    const tableSpecs = FORECAST_STUDIO_ARTIFACTS.filter(([, artifactKey]) => !wanted || wanted.has(artifactKey))
    for (const [tableName, artifactKey] of tableSpecs) {
      const url = forecastArtifactUrl(appApi, workbookId, snapshot, artifactKey)
      if (!url) continue
      const physicalTable = forecastPackageScopedTableName(tableName, packageId)
      const warmKey = forecastArtifactWarmCacheKey(snapshot, physicalTable, artifactKey)
      if (completed?.has(warmKey)) {
        await ensureForecastLogicalView(workerClient, tableName, physicalTable, duckDbOpts)
        forecastStudioPerf('artifact_warm_skip', {
          phase: warmOpts?.phase || 'warm',
          artifact_key: artifactKey,
          table: physicalTable,
          package_id: snapshot.package?.id || null,
        })
        continue
      }
      if (await forecastRelationExists(workerClient, physicalTable, duckDbOpts)) {
        await ensureForecastLogicalView(workerClient, tableName, physicalTable, duckDbOpts)
        completed?.add(warmKey)
        forecastStudioPerf('artifact_warm_reuse_relation', {
          phase: warmOpts?.phase || 'warm',
          artifact_key: artifactKey,
          table: physicalTable,
          package_id: snapshot.package?.id || null,
        })
        continue
      }
      const existing = inFlight?.get(warmKey)
      if (existing) {
        forecastStudioPerf('artifact_warm_joined', {
          phase: warmOpts?.phase || 'warm',
          artifact_key: artifactKey,
          table: physicalTable,
          package_id: snapshot.package?.id || null,
        })
        await existing
        continue
      }
      const startedAt = Date.now()
      forecastStudioPerf('artifact_warm_start', {
        phase: warmOpts?.phase || 'warm',
        artifact_key: artifactKey,
        table: physicalTable,
        package_id: snapshot.package?.id || null,
      })
      const createScopedTable = () => workerClient.duckdb_create_table_from_parquet_urls(physicalTable, [url], {
        ...(duckDbOpts || {}),
        persistent: true,
        persistentTable: true,
        timeoutMs: warmOpts?.timeoutMs || FORECAST_STUDIO_BACKGROUND_ARTIFACT_TIMEOUT_MS,
        caller: 'QuickBooksCfoCore:forecast_artifact_parquet',
        sharedPartCacheKey: forecastArtifactCacheKey(snapshot, artifactKey),
        partBytes: [Number(snapshot?.artifact_materialization?.artifacts?.[artifactKey]?.bytes ?? 0) || 0],
        smallPartBufferThresholdBytes: FORECAST_STUDIO_SMALL_ARTIFACT_BUFFER_THRESHOLD_BYTES,
      })
      const warmPromise = (async () => {
        try {
          await createScopedTable()
        } catch (err) {
          if (!isDuckDbAccessHandleConflict(err)) throw err
          // A stale OPFS sync-access handle can survive a hot reload or an
          // interrupted warm; release it and retry once before surfacing.
          forecastStudioPerf('artifact_warm_handle_conflict_retry', {
            phase: warmOpts?.phase || 'warm',
            artifact_key: artifactKey,
            table: physicalTable,
            package_id: snapshot.package?.id || null,
          })
          await releaseForecastScopedTable(workerClient, physicalTable, duckDbOpts)
          await createScopedTable()
        }
        await ensureForecastLogicalView(workerClient, tableName, physicalTable, duckDbOpts)
      })()
      inFlight?.set(warmKey, warmPromise)
      try {
        await warmPromise
        completed?.add(warmKey)
      } finally {
        if (inFlight?.get(warmKey) === warmPromise) {
          inFlight.delete(warmKey)
        }
      }
      forecastStudioPerf('artifact_warm_done', {
        phase: warmOpts?.phase || 'warm',
        artifact_key: artifactKey,
        table: physicalTable,
        package_id: snapshot.package?.id || null,
        elapsed_ms: Date.now() - startedAt,
      })
    }
    return
  }
  if (typeof workerClient.duckdb_create_temp_table_from_rows !== 'function') return
  // The row fallback writes plain tables under the base names, so any logical
  // views from the parquet path must go first.
  await dropForecastLogicalViews(workerClient, duckDbOpts)
  const opts = {
    ...(duckDbOpts || {}),
    persistent: true,
    dropIfExists: true,
    timeoutMs: 180000,
    caller: 'QuickBooksCfoCore:forecast_snapshot',
  }
  await workerClient.duckdb_create_temp_table_from_rows('fg_qbo_fpa_forecast_package_snapshot', [
    { name: 'package_id', type: 'VARCHAR' },
    { name: 'workbook_id', type: 'VARCHAR' },
    { name: 'connection_id', type: 'VARCHAR' },
    { name: 'scenario_key', type: 'VARCHAR' },
    { name: 'as_of_date', type: 'VARCHAR' },
    { name: 'start_date', type: 'VARCHAR' },
    { name: 'end_date_exclusive', type: 'VARCHAR' },
    { name: 'daily_row_count', type: 'DOUBLE' },
    { name: 'line_count', type: 'DOUBLE' },
  ], [{
    package_id: snapshot.package?.id || '',
    workbook_id: snapshot.package?.workbook_id || '',
    connection_id: snapshot.package?.connection_id || '',
    scenario_key: snapshot.scenario_key || snapshot.package?.active_scenario_key || 'base',
    as_of_date: snapshot.as_of_date || '',
    start_date: snapshot.start_date || '',
    end_date_exclusive: snapshot.end_date_exclusive || '',
    daily_row_count: snapshot.cash_daily?.length || 0,
    line_count: snapshot.line_metadata?.length || 0,
  }], opts)
  await workerClient.duckdb_create_temp_table_from_rows('fg_qbo_fpa_forecast_cash_daily', [
    { name: 'forecast_date', type: 'VARCHAR' },
    { name: 'cash_event_type', type: 'VARCHAR' },
    { name: 'source_type', type: 'VARCHAR' },
    { name: 'source_id', type: 'VARCHAR' },
    { name: 'counterparty_name', type: 'VARCHAR' },
    { name: 'expected_amount', type: 'DOUBLE' },
    { name: 'low_amount', type: 'DOUBLE' },
    { name: 'high_amount', type: 'DOUBLE' },
    { name: 'confidence', type: 'DOUBLE' },
    { name: 'evidence_json', type: 'VARCHAR' },
  ], (snapshot.cash_daily || []).map((row) => ({
    forecast_date: row.forecast_date || '',
    cash_event_type: row.cash_event_type || '',
    source_type: row.source_type || '',
    source_id: row.source_id || '',
    counterparty_name: row.counterparty_name || '',
    expected_amount: Number(row.expected_amount || 0),
    low_amount: row.low_amount == null ? null : Number(row.low_amount),
    high_amount: row.high_amount == null ? null : Number(row.high_amount),
    confidence: row.confidence == null ? null : Number(row.confidence),
    evidence_json: JSON.stringify(row.evidence_json || {}),
  })), opts)
  await workerClient.duckdb_create_temp_table_from_rows('fg_qbo_fpa_forecast_layout_node', [
    { name: 'layout_version_id', type: 'VARCHAR' },
    { name: 'package_id', type: 'VARCHAR' },
    { name: 'node_id', type: 'VARCHAR' },
    { name: 'parent_node_id', type: 'VARCHAR' },
    { name: 'row_type', type: 'VARCHAR' },
    { name: 'statement_type', type: 'VARCHAR' },
    { name: 'label', type: 'VARCHAR' },
    { name: 'level', type: 'DOUBLE' },
    { name: 'account_id', type: 'VARCHAR' },
    { name: 'account_number', type: 'VARCHAR' },
    { name: 'cash_flow_section', type: 'VARCHAR' },
    { name: 'qbo_account_type', type: 'VARCHAR' },
    { name: 'qbo_account_sub_type', type: 'VARCHAR' },
    { name: 'qbo_classification', type: 'VARCHAR' },
    { name: 'normal_balance', type: 'VARCHAR' },
    { name: 'is_total', type: 'BOOLEAN' },
    { name: 'is_formula', type: 'BOOLEAN' },
    { name: 'sort_order', type: 'DOUBLE' },
  ], (snapshot.layout_nodes || []).map((node) => ({
    layout_version_id: node.layout_version_id || '',
    package_id: snapshot.package?.id || '',
    node_id: node.node_id || '',
    parent_node_id: node.parent_node_id || null,
    row_type: node.row_type || '',
    statement_type: node.statement_type || '',
    label: node.label || '',
    level: Number(node.level || 0),
    account_id: node.account_id || null,
    account_number: node.account_number || null,
    cash_flow_section: node.cash_flow_section || null,
    qbo_account_type: node.qbo_account_type || null,
    qbo_account_sub_type: node.qbo_account_sub_type || null,
    qbo_classification: node.qbo_classification || null,
    normal_balance: node.normal_balance || null,
    is_total: Boolean(node.is_total),
    is_formula: Boolean(node.is_formula),
    sort_order: Number(node.sort_order || 0),
  })), opts)
  await workerClient.duckdb_create_temp_table_from_rows('fg_qbo_fpa_forecast_line_metadata', [
    { name: 'id', type: 'VARCHAR' },
    { name: 'package_id', type: 'VARCHAR' },
    { name: 'statement_type', type: 'VARCHAR' },
    { name: 'node_id', type: 'VARCHAR' },
    { name: 'period_start', type: 'VARCHAR' },
    { name: 'period_grain', type: 'VARCHAR' },
    { name: 'scenario_key', type: 'VARCHAR' },
    { name: 'forecast_basis', type: 'VARCHAR' },
    { name: 'confidence', type: 'DOUBLE' },
    { name: 'generated_value', type: 'DOUBLE' },
    { name: 'final_value', type: 'DOUBLE' },
    { name: 'low_value', type: 'DOUBLE' },
    { name: 'high_value', type: 'DOUBLE' },
  ], (snapshot.line_metadata || []).map((line) => ({
    id: line.id || '',
    package_id: line.package_id || '',
    statement_type: line.statement_type || '',
    node_id: line.node_id || '',
    period_start: line.period_start || '',
    period_grain: line.period_grain || 'month',
    scenario_key: line.scenario_key || snapshot.scenario_key || 'base',
    forecast_basis: line.forecast_basis || '',
    confidence: line.confidence == null ? null : Number(line.confidence),
    generated_value: line.generated_value == null ? null : Number(line.generated_value),
    final_value: line.final_value == null ? null : Number(line.final_value),
    low_value: line.low_value == null ? null : Number(line.low_value),
    high_value: line.high_value == null ? null : Number(line.high_value),
  })), opts)
}

// ---------------------------------------------------------------------------
// Business-keyed user deltas: edits/review states fetched from Postgres and
// overlaid onto the package's artifact tables via `_effective` views.
// ---------------------------------------------------------------------------

export const FORECAST_USER_DELTAS_TABLE = 'fg_qbo_fpa_forecast_user_deltas'

export const forecastLineBusinessKey = (
  periodGrain: string | null | undefined,
  statementType: string,
  nodeId: string,
  periodStart: string,
): string => `line|${trimStr(periodGrain) || 'month'}|${statementType}|${nodeId}|${periodStart.slice(0, 10)}`

export const forecastCashWeekBusinessKey = (
  weekStart: string,
  cashEventType: string,
  sourceKey: string | null | undefined,
): string => `cash_week|${weekStart.slice(0, 10)}|${cashEventType}|${sourceKey || ''}`

export const forecastDriverBusinessKey = (
  statementType: string,
  nodeId: string,
  periodStart: string,
  driverKey: string,
): string => `driver|${statementType}|${nodeId}|${periodStart.slice(0, 10)}|${driverKey}`

export const warmUserDeltasInDuckDb = async (
  workerClient: AppWorkerClient | null | undefined,
  deltas: QuickBooksFpaMlUserDelta[],
  duckDbOpts: Record<string, any> | null | undefined,
): Promise<void> => {
  if (!workerClient || typeof workerClient.duckdb_create_temp_table_from_rows !== 'function') return
  await workerClient.duckdb_create_temp_table_from_rows(FORECAST_USER_DELTAS_TABLE, [
    { name: 'business_key', type: 'VARCHAR' },
    { name: 'target_kind', type: 'VARCHAR' },
    { name: 'scenario_key', type: 'VARCHAR' },
    { name: 'statement_type', type: 'VARCHAR' },
    { name: 'node_id', type: 'VARCHAR' },
    { name: 'period_grain', type: 'VARCHAR' },
    { name: 'period_start', type: 'VARCHAR' },
    { name: 'week_start', type: 'VARCHAR' },
    { name: 'cash_event_type', type: 'VARCHAR' },
    { name: 'source_key', type: 'VARCHAR' },
    { name: 'driver_key', type: 'VARCHAR' },
    { name: 'value_override', type: 'DOUBLE' },
    { name: 'low_override', type: 'DOUBLE' },
    { name: 'high_override', type: 'DOUBLE' },
    { name: 'date_override', type: 'VARCHAR' },
    { name: 'feedback_state', type: 'VARCHAR' },
    { name: 'review_note', type: 'VARCHAR' },
    { name: 'revision', type: 'DOUBLE' },
    { name: 'updated_at', type: 'VARCHAR' },
  ], (deltas || []).map((delta) => ({
    business_key: delta.business_key || '',
    target_kind: delta.target_kind || '',
    scenario_key: delta.scenario_key || 'base',
    statement_type: delta.statement_type || null,
    node_id: delta.node_id || null,
    period_grain: delta.period_grain || null,
    period_start: delta.period_start || null,
    week_start: delta.week_start || null,
    cash_event_type: delta.cash_event_type || null,
    source_key: delta.source_key ?? null,
    driver_key: delta.driver_key || null,
    value_override: delta.value_override == null ? null : Number(delta.value_override),
    low_override: delta.low_override == null ? null : Number(delta.low_override),
    high_override: delta.high_override == null ? null : Number(delta.high_override),
    date_override: delta.date_override || null,
    feedback_state: delta.feedback_state || 'unreviewed',
    review_note: delta.review_note || null,
    revision: Number(delta.revision || 1),
    updated_at: delta.updated_at || null,
  })), {
    ...(duckDbOpts || {}),
    persistent: false,
    dropIfExists: true,
    timeoutMs: 60000,
    caller: 'QuickBooksCfoCore:forecast_user_deltas',
  })
}

// Every artifact-side column is string-typed parquet, so the join keys and
// value coalesces below CAST explicitly; a missed cast silently null-joins
// and "loses" deltas.
export const forecastLineEffectiveViewSql = (): string => `
  CREATE OR REPLACE VIEW fg_qbo_fpa_forecast_line_effective AS
  SELECT m.*,
         d.value_override,
         d.low_override,
         d.high_override,
         COALESCE(d.feedback_state, 'unreviewed') AS user_feedback_state,
         d.review_note AS user_review_note,
         d.revision AS delta_revision,
         COALESCE(d.value_override, TRY_CAST(m.final_value AS DOUBLE), TRY_CAST(m.generated_value AS DOUBLE)) AS effective_value,
         COALESCE(d.low_override, TRY_CAST(m.low_value AS DOUBLE)) AS effective_low,
         COALESCE(d.high_override, TRY_CAST(m.high_value AS DOUBLE)) AS effective_high
  FROM fg_qbo_fpa_forecast_line_metadata m
  LEFT JOIN ${FORECAST_USER_DELTAS_TABLE} d
    ON d.target_kind = 'line'
   AND d.scenario_key = COALESCE(NULLIF(CAST(m.scenario_key AS VARCHAR), ''), 'base')
   AND COALESCE(d.period_grain, 'month') = COALESCE(NULLIF(CAST(m.period_grain AS VARCHAR), ''), 'month')
   AND d.statement_type = CAST(m.statement_type AS VARCHAR)
   AND d.node_id = CAST(m.node_id AS VARCHAR)
   AND TRY_CAST(d.period_start AS DATE) = TRY_CAST(m.period_start AS DATE)
`

export const forecastCashWeeklyEffectiveViewSql = (): string => `
  CREATE OR REPLACE VIEW fg_qbo_fpa_forecast_cash_weekly_effective AS
  SELECT w.*,
         d.value_override,
         d.low_override,
         d.high_override,
         COALESCE(d.feedback_state, 'unreviewed') AS user_feedback_state,
         d.review_note AS user_review_note,
         d.revision AS delta_revision,
         COALESCE(d.value_override, TRY_CAST(w.expected_amount AS DOUBLE)) AS effective_amount,
         COALESCE(d.low_override, TRY_CAST(w.low_amount AS DOUBLE)) AS effective_low,
         COALESCE(d.high_override, TRY_CAST(w.high_amount AS DOUBLE)) AS effective_high,
         COALESCE(TRY_CAST(d.date_override AS DATE), TRY_CAST(w.week_start AS DATE)) AS effective_week_start
  FROM fg_qbo_fpa_forecast_cash_weekly w
  LEFT JOIN ${FORECAST_USER_DELTAS_TABLE} d
    ON d.target_kind = 'cash_week'
   AND d.scenario_key = COALESCE(NULLIF(CAST(w.scenario_key AS VARCHAR), ''), 'base')
   AND TRY_CAST(d.week_start AS DATE) = TRY_CAST(w.week_start AS DATE)
   AND d.cash_event_type = CAST(w.cash_event_type AS VARCHAR)
   AND COALESCE(d.source_key, '') = COALESCE(CAST(w.source_key AS VARCHAR), '')
`

export const forecastDriverEffectiveViewSql = (): string => `
  CREATE OR REPLACE VIEW fg_qbo_fpa_forecast_driver_effective AS
  SELECT i.*,
         COALESCE(d.feedback_state, 'unreviewed') AS user_feedback_state,
         d.review_note AS user_review_note,
         d.revision AS delta_revision
  FROM fg_qbo_fpa_forecast_driver_impacts i
  LEFT JOIN ${FORECAST_USER_DELTAS_TABLE} d
    ON d.target_kind = 'driver'
   AND d.scenario_key = COALESCE(NULLIF(CAST(i.scenario_key AS VARCHAR), ''), 'base')
   AND d.statement_type = CAST(i.statement_type AS VARCHAR)
   AND d.node_id = CAST(i.node_id AS VARCHAR)
   AND TRY_CAST(d.period_start AS DATE) = TRY_CAST(i.period_start AS DATE)
   AND d.driver_key = CAST(i.driver_key AS VARCHAR)
`

// Create the `_effective` views over whatever logical tables are warmed. Each
// view is independent: a surface that only warmed line_metadata still gets
// its line view even though the weekly/driver tables are absent.
export const ensureForecastEffectiveViews = async (
  workerClient: AppWorkerClient | null | undefined,
  duckDbOpts: Record<string, any> | null | undefined,
  views: Array<'line' | 'cash_weekly' | 'driver'> = ['line', 'cash_weekly', 'driver'],
): Promise<void> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') return
  const opts = forecastDuckDbStatementOpts(duckDbOpts, 'QuickBooksCfoCore:forecast_effective_views')
  const statements: Array<['line' | 'cash_weekly' | 'driver', string]> = [
    ['line', forecastLineEffectiveViewSql()],
    ['cash_weekly', forecastCashWeeklyEffectiveViewSql()],
    ['driver', forecastDriverEffectiveViewSql()],
  ]
  for (const [view, sql] of statements) {
    if (!views.includes(view)) continue
    // Hard fail: a surface that asked for this view cannot render correctly
    // without it, and a missing view must never be papered over.
    await workerClient.duckdb_run_sql(sql, opts)
  }
}

// Deltas whose business keys no longer exist in the current package's
// artifacts (node removed, week scrolled out of horizon). The UI offers these
// for cleanup via DELETE /forecast-ml/deltas/:id.
export const computeStaleForecastDeltaKeys = (
  deltas: Array<Pick<QuickBooksFpaMlUserDelta, 'business_key'>>,
  presentArtifactKeys: Iterable<string>,
): string[] => {
  const present = new Set(presentArtifactKeys)
  const stale: string[] = []
  for (const delta of deltas || []) {
    const key = trimStr(delta?.business_key)
    if (key && !present.has(key)) stale.push(key)
  }
  return stale
}

// Business keys present in the warmed artifact tables, per delta target kind.
// Only the kinds whose artifacts are actually warmed can be assessed, so the
// caller restricts the delta list to `checkedKinds` before comparing.
const queryPresentForecastBusinessKeys = async (
  workerClient: AppWorkerClient,
  artifactKeys: ForecastArtifactKey[],
  duckDbOpts: Record<string, any> | null | undefined,
): Promise<{ presentKeys: Set<string>; checkedKinds: Set<string> }> => {
  const presentKeys = new Set<string>()
  const checkedKinds = new Set<string>()
  const runKeyQuery = async (sql: string): Promise<Record<string, any>[]> => {
    const result = await workerClient.duckdb_run_sql(sql, {
      ...(duckDbOpts || {}),
      previewRowLimit: 50000,
      timeoutMs: 30000,
      caller: 'QuickBooksCfoCore:forecast_stale_delta_keys',
    })
    return forecastDuckDbRowsAsObjects(result)
  }
  if (artifactKeys.includes('line_metadata')) {
    const rows = await runKeyQuery(`
      SELECT DISTINCT
        COALESCE(NULLIF(CAST(period_grain AS VARCHAR), ''), 'month') AS period_grain,
        CAST(statement_type AS VARCHAR) AS statement_type,
        CAST(node_id AS VARCHAR) AS node_id,
        strftime(TRY_CAST(period_start AS DATE), '%Y-%m-%d') AS period_start
      FROM fg_qbo_fpa_forecast_line_metadata
    `)
    for (const row of rows) {
      const periodStart = trimStr(row.period_start)
      if (!periodStart) continue
      presentKeys.add(forecastLineBusinessKey(String(row.period_grain || 'month'), String(row.statement_type || ''), String(row.node_id || ''), periodStart))
    }
    checkedKinds.add('line')
  }
  if (artifactKeys.includes('cash_weekly')) {
    const rows = await runKeyQuery(`
      SELECT DISTINCT
        strftime(TRY_CAST(week_start AS DATE), '%Y-%m-%d') AS week_start,
        CAST(cash_event_type AS VARCHAR) AS cash_event_type,
        COALESCE(CAST(source_key AS VARCHAR), '') AS source_key
      FROM fg_qbo_fpa_forecast_cash_weekly
    `)
    for (const row of rows) {
      const weekStart = trimStr(row.week_start)
      if (!weekStart) continue
      presentKeys.add(forecastCashWeekBusinessKey(weekStart, String(row.cash_event_type || ''), String(row.source_key || '')))
    }
    checkedKinds.add('cash_week')
  }
  if (artifactKeys.includes('driver_impacts')) {
    const rows = await runKeyQuery(`
      SELECT DISTINCT
        CAST(statement_type AS VARCHAR) AS statement_type,
        CAST(node_id AS VARCHAR) AS node_id,
        strftime(TRY_CAST(period_start AS DATE), '%Y-%m-%d') AS period_start,
        CAST(driver_key AS VARCHAR) AS driver_key
      FROM fg_qbo_fpa_forecast_driver_impacts
    `)
    for (const row of rows) {
      const periodStart = trimStr(row.period_start)
      if (!periodStart) continue
      presentKeys.add(forecastDriverBusinessKey(String(row.statement_type || ''), String(row.node_id || ''), periodStart, String(row.driver_key || '')))
    }
    checkedKinds.add('driver')
  }
  return { presentKeys, checkedKinds }
}

// ---------------------------------------------------------------------------
// Shared row-level loaders: warm a package's tables + deltas, then query the
// `_effective` views into the legacy endpoint row shapes so existing grids
// can swap their data source without changing render code. Row ids are the
// business key (artifact rows have no Postgres ids) — edit/review calls send
// the same business-key fields.
// ---------------------------------------------------------------------------

const sqlQuote = (value: string): string => `'${String(value || '').replace(/'/g, "''")}'`

const parseJsonish = (raw: any): any => {
  if (raw && typeof raw === 'object') return raw
  try {
    return JSON.parse(String(raw ?? 'null')) ?? undefined
  } catch {
    return undefined
  }
}

const numberOrNull = (raw: any): number | null => {
  if (raw == null || raw === '') return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

export type ForecastDeltaWarmContext = {
  deltas: import('../../../data/ServerApiClient').QuickBooksFpaMlUserDelta[]
  staleKeys: string[]
}

// Warm the artifact tables a surface needs plus the user-delta overlay and
// `_effective` views. Safe to call repeatedly: warmed tables short-circuit on
// their cache keys and views are CREATE OR REPLACE.
export const warmForecastPackageForQueries = async (
  appApi: ServerApiClient,
  workerClient: AppWorkerClient | null | undefined,
  workbookId: string | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  duckDbOpts: Record<string, any> | null,
  artifactKeys: ForecastArtifactKey[],
  warmOpts?: {
    timeoutMs?: number
    phase?: string
    inFlight?: Map<string, Promise<void>>
    completed?: Set<string>
    scenarioKey?: string | null
  },
): Promise<ForecastDeltaWarmContext> => {
  await warmForecastSnapshotInDuckDb(appApi, workerClient, workbookId, snapshot, duckDbOpts, {
    artifactKeys,
    timeoutMs: warmOpts?.timeoutMs,
    phase: warmOpts?.phase || 'shared_loader',
    inFlight: warmOpts?.inFlight,
    completed: warmOpts?.completed,
  })
  // The delta overlay is part of the data contract: a failed fetch is a hard
  // failure — never silently render the base artifact with user edits missing.
  const response = await appApi.getQuickBooksFpaMlDeltas(trimStr(workbookId), {
    scenario_key: warmOpts?.scenarioKey || null,
  })
  const deltas = response?.deltas || []
  await warmUserDeltasInDuckDb(workerClient, deltas, duckDbOpts)
  const views: Array<'line' | 'cash_weekly' | 'driver'> = []
  if (artifactKeys.includes('line_metadata')) views.push('line')
  if (artifactKeys.includes('cash_weekly')) views.push('cash_weekly')
  if (artifactKeys.includes('driver_impacts')) views.push('driver')
  await ensureForecastEffectiveViews(workerClient, duckDbOpts, views)
  // Staleness detection is advisory cleanup, not part of the render contract:
  // a failed key scan must never block the forecast load.
  let staleKeys: string[] = []
  if (deltas.length && workerClient && typeof workerClient.duckdb_run_sql === 'function') {
    try {
      const { presentKeys, checkedKinds } = await queryPresentForecastBusinessKeys(workerClient, artifactKeys, duckDbOpts)
      staleKeys = computeStaleForecastDeltaKeys(
        deltas.filter((delta) => checkedKinds.has(trimStr(delta.target_kind))),
        presentKeys,
      )
    } catch (err) {
      forecastStudioPerf('stale_delta_key_scan_failed', { message: trimStr((err as any)?.message || err) })
    }
  }
  return { deltas, staleKeys }
}

export const queryForecastLinesEffective = async (
  workerClient: AppWorkerClient | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  scenarioKey: string,
  duckDbOpts: Record<string, any> | null | undefined,
  opts?: { statementType?: string | null; limit?: number | null },
): Promise<import('../../../data/ServerApiClient').QuickBooksFpaForecastLineMetadata[]> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') return []
  const packageId = trimStr(snapshot.package?.id)
  if (!packageId) return []
  const statementFilter = trimStr(opts?.statementType)
    ? `AND CAST(m.statement_type AS VARCHAR) = ${sqlQuote(trimStr(opts?.statementType))}`
    : ''
  const result = await workerClient.duckdb_run_sql(`
    SELECT
      CAST(m.statement_type AS VARCHAR) AS statement_type,
      CAST(m.node_id AS VARCHAR) AS node_id,
      CAST(m.period_start AS VARCHAR) AS period_start,
      COALESCE(NULLIF(CAST(m.period_grain AS VARCHAR), ''), 'month') AS period_grain,
      COALESCE(NULLIF(CAST(m.scenario_key AS VARCHAR), ''), 'base') AS scenario_key,
      CAST(m.forecast_basis AS VARCHAR) AS forecast_basis,
      CAST(m.model_family AS VARCHAR) AS model_family,
      TRY_CAST(m.confidence AS DOUBLE) AS confidence,
      m.effective_low AS low_value,
      m.effective_high AS high_value,
      TRY_CAST(m.generated_value AS DOUBLE) AS generated_value,
      m.effective_value AS final_value,
      CAST(m.evidence_json AS VARCHAR) AS evidence_json,
      CAST(m.driver_json AS VARCHAR) AS driver_json,
      CAST(m.warning_json AS VARCHAR) AS warning_json,
      CAST(m.backtest_json AS VARCHAR) AS backtest_json,
      m.user_feedback_state AS feedback_state,
      m.user_review_note AS review_note,
      CAST(m.generated_at AS VARCHAR) AS created_at
    FROM fg_qbo_fpa_forecast_line_effective m
    WHERE COALESCE(NULLIF(CAST(m.scenario_key AS VARCHAR), ''), 'base') = ${sqlQuote(scenarioKey)}
      ${statementFilter}
    ORDER BY statement_type, period_start, confidence DESC NULLS LAST
    LIMIT ${Math.max(1, Math.floor(Number(opts?.limit ?? 5000)))}
  `, {
    ...(duckDbOpts || {}),
    previewRowLimit: Math.max(1, Math.floor(Number(opts?.limit ?? 5000))),
    timeoutMs: 60000,
    caller: 'QuickBooksCfoCore:forecast_lines_effective_query',
  })
  return forecastDuckDbRowsAsObjects(result).map((row) => {
    const periodStart = String(row.period_start || '').slice(0, 10)
    const statementType = String(row.statement_type || '')
    const nodeId = String(row.node_id || '')
    const periodGrain = String(row.period_grain || 'month')
    return {
      id: forecastLineBusinessKey(periodGrain, statementType, nodeId, periodStart),
      package_id: packageId,
      statement_type: statementType,
      node_id: nodeId,
      period_start: periodStart,
      period_grain: periodGrain,
      scenario_key: String(row.scenario_key || 'base'),
      forecast_basis: String(row.forecast_basis || ''),
      model_family: row.model_family == null ? null : String(row.model_family),
      confidence: numberOrNull(row.confidence),
      low_value: numberOrNull(row.low_value),
      high_value: numberOrNull(row.high_value),
      generated_value: numberOrNull(row.generated_value),
      final_value: numberOrNull(row.final_value),
      backtest_json: parseJsonish(row.backtest_json),
      evidence_json: parseJsonish(row.evidence_json),
      driver_json: parseJsonish(row.driver_json),
      warning_json: parseJsonish(row.warning_json),
      feedback_state: String(row.feedback_state || 'unreviewed'),
      review_note: row.review_note == null ? null : String(row.review_note),
      created_at: String(row.created_at || ''),
    }
  })
}

// Single-line drill for the "why this number" drawer: the `_effective` row
// for one node+period including the explainability JSON columns.
export const queryForecastLineExplain = async (
  workerClient: AppWorkerClient | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  scenarioKey: string,
  duckDbOpts: Record<string, any> | null | undefined,
  target: { nodeId: string; periodStart: string; statementType?: string | null },
): Promise<import('../../../data/ServerApiClient').QuickBooksFpaForecastLineMetadata | null> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') return null
  const packageId = trimStr(snapshot.package?.id)
  const nodeId = trimStr(target.nodeId)
  const periodStart = trimStr(target.periodStart).slice(0, 10)
  if (!packageId || !nodeId || !periodStart) return null
  const statementFilter = trimStr(target.statementType)
    ? `AND CAST(m.statement_type AS VARCHAR) = ${sqlQuote(trimStr(target.statementType))}`
    : ''
  const result = await workerClient.duckdb_run_sql(`
    SELECT
      CAST(m.statement_type AS VARCHAR) AS statement_type,
      CAST(m.node_id AS VARCHAR) AS node_id,
      CAST(m.period_start AS VARCHAR) AS period_start,
      COALESCE(NULLIF(CAST(m.period_grain AS VARCHAR), ''), 'month') AS period_grain,
      COALESCE(NULLIF(CAST(m.scenario_key AS VARCHAR), ''), 'base') AS scenario_key,
      CAST(m.forecast_basis AS VARCHAR) AS forecast_basis,
      CAST(m.model_family AS VARCHAR) AS model_family,
      TRY_CAST(m.confidence AS DOUBLE) AS confidence,
      m.effective_low AS low_value,
      m.effective_high AS high_value,
      TRY_CAST(m.generated_value AS DOUBLE) AS generated_value,
      m.effective_value AS final_value,
      CAST(m.evidence_json AS VARCHAR) AS evidence_json,
      CAST(m.driver_json AS VARCHAR) AS driver_json,
      CAST(m.warning_json AS VARCHAR) AS warning_json,
      CAST(m.backtest_json AS VARCHAR) AS backtest_json,
      m.user_feedback_state AS feedback_state,
      m.user_review_note AS review_note,
      CAST(m.generated_at AS VARCHAR) AS created_at
    FROM fg_qbo_fpa_forecast_line_effective m
    WHERE COALESCE(NULLIF(CAST(m.scenario_key AS VARCHAR), ''), 'base') = ${sqlQuote(trimStr(scenarioKey) || 'base')}
      AND CAST(m.node_id AS VARCHAR) = ${sqlQuote(nodeId)}
      AND TRY_CAST(m.period_start AS DATE) = TRY_CAST(${sqlQuote(periodStart)} AS DATE)
      ${statementFilter}
    LIMIT 1
  `, {
    ...(duckDbOpts || {}),
    previewRowLimit: 1,
    timeoutMs: 30000,
    caller: 'QuickBooksCfoCore:forecast_line_explain_query',
  })
  const row = forecastDuckDbRowsAsObjects(result)[0]
  if (!row) return null
  const statementType = String(row.statement_type || '')
  const periodGrain = String(row.period_grain || 'month')
  const rowPeriodStart = String(row.period_start || '').slice(0, 10)
  return {
    id: forecastLineBusinessKey(periodGrain, statementType, nodeId, rowPeriodStart),
    package_id: packageId,
    statement_type: statementType,
    node_id: nodeId,
    period_start: rowPeriodStart,
    period_grain: periodGrain,
    scenario_key: String(row.scenario_key || 'base'),
    forecast_basis: String(row.forecast_basis || ''),
    model_family: row.model_family == null ? null : String(row.model_family),
    confidence: numberOrNull(row.confidence),
    low_value: numberOrNull(row.low_value),
    high_value: numberOrNull(row.high_value),
    generated_value: numberOrNull(row.generated_value),
    final_value: numberOrNull(row.final_value),
    backtest_json: parseJsonish(row.backtest_json),
    evidence_json: parseJsonish(row.evidence_json),
    driver_json: parseJsonish(row.driver_json),
    warning_json: parseJsonish(row.warning_json),
    feedback_state: String(row.feedback_state || 'unreviewed'),
    review_note: row.review_note == null ? null : String(row.review_note),
    created_at: String(row.created_at || ''),
  }
}

export const queryForecastCashWeeksEffective = async (
  workerClient: AppWorkerClient | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  scenarioKey: string,
  duckDbOpts: Record<string, any> | null | undefined,
  opts?: { limit?: number | null },
): Promise<import('../../../data/ServerApiClient').QuickBooksFpaMlCashWeek[]> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') return []
  const packageId = trimStr(snapshot.package?.id)
  if (!packageId) return []
  const result = await workerClient.duckdb_run_sql(`
    SELECT
      CAST(w.week_start AS VARCHAR) AS original_week_start,
      CAST(w.effective_week_start AS VARCHAR) AS week_start,
      CAST(w.cash_event_type AS VARCHAR) AS cash_event_type,
      CAST(w.source_type AS VARCHAR) AS source_type,
      CAST(w.source_id AS VARCHAR) AS source_id,
      COALESCE(CAST(w.source_key AS VARCHAR), '') AS source_key,
      CAST(w.counterparty_id AS VARCHAR) AS counterparty_id,
      CAST(w.counterparty_name AS VARCHAR) AS counterparty_name,
      COALESCE(NULLIF(CAST(w.scenario_key AS VARCHAR), ''), 'base') AS scenario_key,
      w.effective_amount AS expected_amount,
      w.effective_low AS low_amount,
      w.effective_high AS high_amount,
      TRY_CAST(w.confidence AS DOUBLE) AS confidence,
      CAST(w.evidence_json AS VARCHAR) AS evidence_json,
      CAST(w.warning_json AS VARCHAR) AS warning_json,
      w.user_feedback_state AS feedback_state,
      w.user_review_note AS review_note
    FROM fg_qbo_fpa_forecast_cash_weekly_effective w
    WHERE COALESCE(NULLIF(CAST(w.scenario_key AS VARCHAR), ''), 'base') = ${sqlQuote(scenarioKey)}
      AND COALESCE(CAST(w.source_type AS VARCHAR), '') <> 'cash_week_summary'
      AND CAST(w.cash_event_type AS VARCHAR) <> 'net_operating_cash'
    ORDER BY week_start, cash_event_type, source_id NULLS LAST
    LIMIT ${Math.max(1, Math.floor(Number(opts?.limit ?? 2000)))}
  `, {
    ...(duckDbOpts || {}),
    previewRowLimit: Math.max(1, Math.floor(Number(opts?.limit ?? 2000))),
    timeoutMs: 60000,
    caller: 'QuickBooksCfoCore:forecast_cash_weeks_effective_query',
  })
  return forecastDuckDbRowsAsObjects(result).map((row) => {
    const originalWeekStart = String(row.original_week_start || '').slice(0, 10)
    const cashEventType = String(row.cash_event_type || '')
    const sourceKey = String(row.source_key || '')
    return {
      // The business key keeps the ORIGINAL week so timing moves stay one row.
      id: forecastCashWeekBusinessKey(originalWeekStart, cashEventType, sourceKey),
      package_id: packageId,
      scenario_key: String(row.scenario_key || 'base'),
      week_start: String(row.week_start || originalWeekStart).slice(0, 10),
      cash_event_type: cashEventType,
      source_type: row.source_type == null ? null : String(row.source_type),
      source_id: row.source_id == null ? null : String(row.source_id),
      source_key: sourceKey,
      counterparty_id: row.counterparty_id == null ? null : String(row.counterparty_id),
      counterparty_name: row.counterparty_name == null ? null : String(row.counterparty_name),
      expected_amount: String(numberOrNull(row.expected_amount) ?? 0),
      low_amount: row.low_amount == null ? null : String(numberOrNull(row.low_amount) ?? ''),
      high_amount: row.high_amount == null ? null : String(numberOrNull(row.high_amount) ?? ''),
      confidence: numberOrNull(row.confidence),
      evidence_json: parseJsonish(row.evidence_json),
      warning_json: parseJsonish(row.warning_json),
      feedback_state: String(row.feedback_state || 'unreviewed'),
      review_note: row.review_note == null ? null : String(row.review_note),
      derived: false,
    }
  })
}

export const queryForecastDriversEffective = async (
  workerClient: AppWorkerClient | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  scenarioKey: string,
  duckDbOpts: Record<string, any> | null | undefined,
  opts?: { limit?: number | null },
): Promise<import('../../../data/ServerApiClient').QuickBooksFpaMlDriverImpact[]> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') return []
  const packageId = trimStr(snapshot.package?.id)
  if (!packageId) return []
  const result = await workerClient.duckdb_run_sql(`
    SELECT
      CAST(i.statement_type AS VARCHAR) AS statement_type,
      CAST(i.node_id AS VARCHAR) AS node_id,
      CAST(i.period_start AS VARCHAR) AS period_start,
      COALESCE(NULLIF(CAST(i.scenario_key AS VARCHAR), ''), 'base') AS scenario_key,
      CAST(i.driver_key AS VARCHAR) AS driver_key,
      CAST(i.driver_label AS VARCHAR) AS driver_label,
      CAST(i.driver_type AS VARCHAR) AS driver_type,
      TRY_CAST(i.amount_impact AS DOUBLE) AS amount_impact,
      TRY_CAST(i.confidence AS DOUBLE) AS confidence,
      CAST(i.explanation AS VARCHAR) AS explanation,
      CAST(i.evidence_json AS VARCHAR) AS evidence_json,
      i.user_feedback_state AS feedback_state,
      i.user_review_note AS review_note
    FROM fg_qbo_fpa_forecast_driver_effective i
    WHERE COALESCE(NULLIF(CAST(i.scenario_key AS VARCHAR), ''), 'base') = ${sqlQuote(scenarioKey)}
    ORDER BY ABS(TRY_CAST(i.amount_impact AS DOUBLE)) DESC NULLS LAST
    LIMIT ${Math.max(1, Math.floor(Number(opts?.limit ?? 500)))}
  `, {
    ...(duckDbOpts || {}),
    previewRowLimit: Math.max(1, Math.floor(Number(opts?.limit ?? 500))),
    timeoutMs: 60000,
    caller: 'QuickBooksCfoCore:forecast_drivers_effective_query',
  })
  return forecastDuckDbRowsAsObjects(result).map((row) => {
    const statementType = String(row.statement_type || '')
    const nodeId = String(row.node_id || '')
    const periodStart = String(row.period_start || '').slice(0, 10)
    const driverKey = String(row.driver_key || '')
    return {
      id: forecastDriverBusinessKey(statementType, nodeId, periodStart, driverKey),
      package_id: packageId,
      scenario_key: String(row.scenario_key || 'base'),
      statement_type: statementType,
      node_id: nodeId,
      period_start: periodStart,
      driver_key: driverKey,
      driver_label: String(row.driver_label || driverKey),
      driver_type: String(row.driver_type || ''),
      amount_impact: String(numberOrNull(row.amount_impact) ?? 0),
      confidence: numberOrNull(row.confidence),
      explanation: row.explanation == null ? null : String(row.explanation),
      evidence_json: parseJsonish(row.evidence_json),
      feedback_state: String(row.feedback_state || 'unreviewed'),
      review_note: row.review_note == null ? null : String(row.review_note),
      derived: false,
    }
  })
}
